import { useState, useRef, useEffect, useCallback } from "react";
import {
  Bell,
  ChevronDown,
  Building2,
  Search,
  CheckCircle2,
  Clock,
  AlertCircle,
  X,
  ShieldCheck,
  Settings,
  CreditCard,
  LogOut,
  AlertTriangle,
  CreditCard as PaymentIcon,
  Megaphone,
  Zap,
  UserPlus,
  Info,
  Mail,
  MailOpen,
  Trash2,
  Loader2,
  Users,
  FileText,
  BarChart3,
  MessageSquare,
  ArrowRight,
  Landmark,
  HelpCircle,
  Layers,
  Calendar,
  PhoneCall,
  Menu,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { getInitials } from "../../lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../lib/api";
import { getSocket } from "../../lib/socket";
import { sendBrowserNotification } from "../../lib/browserNotification";
import toast from "react-hot-toast";

// ── NOTIFICATION ICON MAP ──
const NOTIF_ICONS = {
  overdue_account: {
    icon: AlertTriangle,
    color: "text-rose-500",
    bg: "bg-rose-50",
  },
  payment_received: {
    icon: PaymentIcon,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
  },
  direct_transfer_notified: {
    icon: Landmark,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  transfer_approved: {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  transfer_rejected: {
    icon: AlertTriangle,
    color: "text-rose-600",
    bg: "bg-rose-50",
  },
  debtor_dispute: {
    icon: HelpCircle,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  portal_dispute: {
    icon: HelpCircle,
    color: "text-amber-600",
    bg: "bg-amber-50",
  },
  restructure_request: {
    icon: Layers,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  restructure_requested: {
    icon: Layers,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  promise_to_pay: {
    icon: Calendar,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  promise_to_pay_recorded: {
    icon: Calendar,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
  },
  collector_assigned: {
    icon: PhoneCall,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
  },
  payment_failed: {
    icon: AlertCircle,
    color: "text-rose-500",
    bg: "bg-rose-50",
  },
  campaign_completed: {
    icon: Megaphone,
    color: "text-blue-500",
    bg: "bg-blue-50",
  },
  campaign_failed: {
    icon: Megaphone,
    color: "text-rose-500",
    bg: "bg-rose-50",
  },
  trial_ending: { icon: Clock, color: "text-amber-500", bg: "bg-amber-50" },
  trial_expired: {
    icon: AlertCircle,
    color: "text-rose-500",
    bg: "bg-rose-50",
  },
  subscription_past_due: {
    icon: CreditCard,
    color: "text-amber-500",
    bg: "bg-amber-50",
  },
  subscription_renewed: {
    icon: CheckCircle2,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
  },
  automation_triggered: {
    icon: Zap,
    color: "text-violet-500",
    bg: "bg-violet-50",
  },
  automation_failed: { icon: Zap, color: "text-rose-500", bg: "bg-rose-50" },
  new_team_member: {
    icon: UserPlus,
    color: "text-indigo-500",
    bg: "bg-indigo-50",
  },
  invite_accepted: {
    icon: UserPlus,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
  },
  limit_warning: { icon: Info, color: "text-amber-500", bg: "bg-amber-50" },
  system_update: { icon: Info, color: "text-blue-500", bg: "bg-blue-50" },
  welcome: {
    icon: CheckCircle2,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
  },
};

const PRIORITY_BADGES = {
  low: "bg-slate-100 text-slate-500",
  normal: "bg-blue-50 text-blue-600",
  high: "bg-amber-50 text-amber-600",
  urgent: "bg-rose-50 text-rose-600",
};

// ── SEARCH RESULT ICONS ──
const SEARCH_ICONS = {
  client: {
    icon: Users,
    color: "text-indigo-500",
    bg: "bg-indigo-50",
    label: "Client",
  },
  plan: {
    icon: FileText,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
    label: "Plan",
  },
  campaign: {
    icon: Megaphone,
    color: "text-blue-500",
    bg: "bg-blue-50",
    label: "Campaign",
  },
  debtRecovery: {
    icon: BarChart3,
    color: "text-amber-500",
    bg: "bg-amber-50",
    label: "Recovery",
  },
  automation: {
    icon: Zap,
    color: "text-violet-500",
    bg: "bg-violet-50",
    label: "Automation",
  },
};

export default function TopBar({ onOpenMobileMenu }) {
  const {
    user,
    organizations,
    activeOrganization,
    switchOrganization,
    logout,
  } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showOrgMenu, setShowOrgMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounce, setSearchDebounce] = useState("");
  const orgRef = useRef(null);
  const userRef = useRef(null);
  const notifRef = useRef(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // ── DEBOUNCE SEARCH ──
  useEffect(() => {
    const timer = setTimeout(() => setSearchDebounce(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── FETCH NOTIFICATIONS ──
  const { data: notifData, isLoading: notifsLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      const res = await api.get("/api/v2/notifications?limit=20");
      return res.data;
    },
    refetchInterval: 30000,
  });

  // ── FETCH UNREAD COUNT ──
  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: async () => {
      const res = await api.get("/api/v2/notifications/unread-count");
      return res.data;
    },
    refetchInterval: 30000,
  });

  // ── REAL-TIME SOCKET.IO NOTIFICATION & BADGE LISTENER ──
  useEffect(() => {
    if (!user) return;
    const socket = getSocket();

    const handleNewNotification = (newNotif) => {
      // Invalidate queries instantly to refresh list, badge, and recovery plans
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["debt-recoveries"] });
      queryClient.invalidateQueries({ queryKey: ["debt-recovery"] });

      if (newNotif?.title) {
        sendBrowserNotification(newNotif.title, {
          body: newNotif.message || "You have a new update in your workspace.",
          icon: "/favicon.ico",
          playSound: true,
          onClick: () => {
            if (newNotif.link) {
              navigate(newNotif.link);
            }
          },
        });

        toast(
          (t) => (
            <div className="flex items-start gap-2.5 text-xs py-0.5">
              <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg shrink-0 mt-0.5">
                <Bell className="h-4 w-4" />
              </div>
              <div className="space-y-0.5">
                <p className="font-bold text-slate-900 leading-snug">{newNotif.title}</p>
                <p className="text-slate-600 text-[11px] leading-tight line-clamp-2">{newNotif.message}</p>
              </div>
            </div>
          ),
          {
            duration: 6000,
            id: `notif-${newNotif._id || Date.now()}`,
          }
        );
      }
    };

    const handleUnreadCount = (data) => {
      if (data && typeof data.count === "number") {
        queryClient.setQueryData(["notifications-unread"], { count: data.count });
      }
    };

    const handleOrgNotification = () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
      queryClient.invalidateQueries({ queryKey: ["debt-recoveries"] });
      queryClient.invalidateQueries({ queryKey: ["debt-recovery"] });
    };

    socket.on("notification:new", handleNewNotification);
    socket.on("notification:unread_count", handleUnreadCount);
    socket.on("org:notification", handleOrgNotification);

    return () => {
      socket.off("notification:new", handleNewNotification);
      socket.off("notification:unread_count", handleUnreadCount);
      socket.off("org:notification", handleOrgNotification);
    };
  }, [user, queryClient]);

  // ── SEARCH QUERY ──
  const { data: searchResults, isLoading: searchLoading } = useQuery({
    queryKey: ["global-search", searchDebounce],
    queryFn: async () => {
      if (!searchDebounce || searchDebounce.length < 2) return null;
      const res = await api.get(
        `/api/v2/search?q=${encodeURIComponent(searchDebounce)}`,
      );
      return res.data;
    },
    enabled: searchDebounce.length >= 2,
  });

  const notifications = notifData?.notifications || [];
  const unreadCount = unreadData?.count || 0;
  const results = searchResults?.data || null;

  // ── MUTATIONS ──
  const markReadMutation = useMutation({
    mutationFn: (id) => api.patch(`/api/v2/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => api.patch("/api/v2/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/v1/notifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
    },
  });

  // ── CLICK OUTSIDE ──
  useEffect(() => {
    function handleClickOutside(e) {
      if (orgRef.current && !orgRef.current.contains(e.target))
        setShowOrgMenu(false);
      if (userRef.current && !userRef.current.contains(e.target))
        setShowUserMenu(false);
      if (notifRef.current && !notifRef.current.contains(e.target))
        setShowNotifications(false);
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
        setSearchQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── KEYBOARD SHORTCUT ──
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        setShowSearch(true);
      }
      if (e.key === "Escape") {
        setShowSearch(false);
        setSearchQuery("");
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSwitch = async (orgId) => {
    const result = await switchOrganization(orgId);
    if (result?.success) {
      setShowOrgMenu(false);
      window.location.reload();
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markReadMutation.mutate(notif._id);
    if (notif.metadata?.url) {
      navigate(notif.metadata.url);
      setShowNotifications(false);
    }
  };

  const handleSearchResultClick = (result) => {
    setShowSearch(false);
    setSearchQuery("");
    navigate(result.url);
  };

  const formatNotifTime = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return then.toLocaleDateString();
  };

  const getAllResults = () => {
    if (!results) return [];
    const all = [];
    if (results.clients)
      all.push(...results.clients.map((r) => ({ ...r, type: "client" })));
    if (results.plans)
      all.push(...results.plans.map((r) => ({ ...r, type: "plan" })));
    if (results.campaigns)
      all.push(...results.campaigns.map((r) => ({ ...r, type: "campaign" })));
    if (results.debtRecoveries)
      all.push(
        ...results.debtRecoveries.map((r) => ({ ...r, type: "debtRecovery" })),
      );
    if (results.automations)
      all.push(
        ...results.automations.map((r) => ({ ...r, type: "automation" })),
      );
    return all.slice(0, 10); // max 10 results
  };

  const allResults = getAllResults();

  return (
    <header className="flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 sm:px-6 lg:px-8">
      {/* ── LEFT SECTION (MOBILE MENU BUTTON / SEARCH) ── */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onOpenMobileMenu}
          className="md:hidden p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-all"
          aria-label="Open mobile menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* ── DESKTOP SEARCH ── */}
        <div
          className="hidden md:flex items-center max-w-md flex-1 relative"
          ref={searchRef}
        >
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearch(true);
            }}
            onFocus={() => setShowSearch(true)}
            placeholder="Search clients, plans, campaigns..."
            className="w-full pl-10 pr-10 py-2 text-sm bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all"
          />
          {/* Keyboard shortcut hint */}
          {!searchQuery && (
            <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden lg:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 rounded border border-slate-200">
              <span>⌘</span>
              <span>K</span>
            </kbd>
          )}
          {searchQuery && (
            <button
              onClick={() => {
                setSearchQuery("");
                searchInputRef.current?.focus();
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* ── SEARCH DROPDOWN ── */}
        {showSearch && searchDebounce.length >= 2 && (
          <div className="absolute top-full left-0 mt-2 w-[400px] max-w-[calc(100vw-2rem)] rounded-2xl border border-slate-200 bg-white shadow-xl shadow-slate-200/50 z-50 overflow-hidden">
            {searchLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-indigo-600" />
              </div>
            ) : allResults.length === 0 ? (
              <div className="py-8 text-center">
                <Search className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  No results for "{searchDebounce}"
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  Try a different search term
                </p>
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto py-2">
                {/* Group by type */}
                {results.clients?.length > 0 && (
                  <div className="px-3 py-1">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-1">
                      Clients
                    </p>
                    {results.clients.slice(0, 3).map((client) => (
                      <button
                        key={client._id}
                        onClick={() =>
                          handleSearchResultClick({
                            url: `/clients/${client._id}`,
                          })
                        }
                        className="flex items-center gap-3 w-full px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                          <Users className="h-4 w-4 text-indigo-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {client.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {client.email || client.phone}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {results.plans?.length > 0 && (
                  <div className="px-3 py-1 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-1">
                      Plans
                    </p>
                    {results.plans.slice(0, 3).map((plan) => (
                      <button
                        key={plan._id}
                        onClick={() =>
                          handleSearchResultClick({ url: `/plans?id=${plan._id}` })
                        }
                        className="flex items-center gap-3 w-full px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
                          <FileText className="h-4 w-4 text-emerald-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {plan.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            ₦{plan.totalAmount?.toLocaleString()}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {results.campaigns?.length > 0 && (
                  <div className="px-3 py-1 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-1">
                      Campaigns
                    </p>
                    {results.campaigns.slice(0, 2).map((campaign) => (
                      <button
                        key={campaign._id}
                        onClick={() =>
                          handleSearchResultClick({
                            url: `/campaigns/${campaign._id}`,
                          })
                        }
                        className="flex items-center gap-3 w-full px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                          <Megaphone className="h-4 w-4 text-blue-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {campaign.name}
                          </p>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              campaign.status === "completed"
                                ? "bg-emerald-100 text-emerald-700"
                                : campaign.status === "sending"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-slate-100 text-slate-600"
                            }`}
                          >
                            {campaign.status}
                          </span>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {results.debtRecoveries?.length > 0 && (
                  <div className="px-3 py-1 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-1">
                      Recoveries
                    </p>
                    {results.debtRecoveries.slice(0, 2).map((recovery) => (
                      <button
                        key={recovery._id}
                        onClick={() =>
                          handleSearchResultClick({
                            url: `/debt-recoveries/${recovery._id}`,
                          })
                        }
                        className="flex items-center gap-3 w-full px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
                          <BarChart3 className="h-4 w-4 text-amber-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {recovery.clientId?.name || "Unknown"}
                          </p>
                          <p className="text-xs text-slate-500">
                            Balance: ₦{recovery.balance?.toLocaleString()}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {results.automations?.length > 0 && (
                  <div className="px-3 py-1 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider px-2 mb-1">
                      Automations
                    </p>
                    {results.automations.slice(0, 2).map((automation) => (
                      <button
                        key={automation._id}
                        onClick={() =>
                          handleSearchResultClick({
                            url: `/automations/${automation._id}`,
                          })
                        }
                        className="flex items-center gap-3 w-full px-2 py-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
                      >
                        <div className="h-8 w-8 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">
                          <Zap className="h-4 w-4 text-violet-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {automation.name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {automation.trigger?.type}
                          </p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-slate-300" />
                      </button>
                    ))}
                  </div>
                )}

                {/* View all results */}
                {allResults.length >= 10 && (
                  <div className="border-t border-slate-100 px-3 py-2">
                    <p className="text-xs text-slate-400 text-center">
                      {allResults.length - 10} more results — refine your search
                    </p>
                  </div>
                )}
                {/* <div className="border-t border-slate-100 px-3 py-2">
                  <button
                    onClick={() => {
                      navigate(`/search?q=${encodeURIComponent(searchDebounce)}`);
                      setShowSearch(false);
                      setSearchQuery("");
                    }}
                    className="flex items-center justify-center gap-2 w-full py-2 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors"
                  >
                    <Search className="h-3.5 w-3.5" />
                    View all results for "{searchDebounce}"
                  </button>
                </div> */}
              </div>
            )}
          </div>
        )}
      </div>
    </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2">
        {/* Mobile search button */}
        <button
          onClick={() => {
            setShowSearch(true);
            setTimeout(() => searchInputRef.current?.focus(), 100);
          }}
          className="md:hidden rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 transition-colors"
        >
          <Search className="h-5 w-5" />
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative rounded-xl p-2.5 text-slate-500 hover:bg-slate-100 transition-colors"
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white border-2 border-white animate-pulse">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 w-auto sm:w-96 rounded-2xl border border-slate-200 bg-white py-2 shadow-xl shadow-slate-200/50 z-50">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">
                    Notifications
                  </h3>
                  {unreadCount > 0 && (
                    <p className="text-[11px] text-slate-500">
                      {unreadCount} unread
                    </p>
                  )}
                </div>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllReadMutation.mutate()}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-700 px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifsLoading ? (
                  <div className="py-8 text-center">
                    <Clock className="h-5 w-5 animate-spin text-slate-400 mx-auto mb-2" />
                    <p className="text-xs text-slate-500">Loading...</p>
                  </div>
                ) : notifications.length === 0 ? (
                  <div className="py-8 text-center">
                    <Bell className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">
                      No notifications yet
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      We'll notify you when something happens
                    </p>
                  </div>
                ) : (
                  notifications.map((notif) => {
                    const iconConfig =
                      NOTIF_ICONS[notif.type] || NOTIF_ICONS.system_update;
                    const Icon = iconConfig.icon;

                    return (
                      <div
                        key={notif._id}
                        className={`group relative flex gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          !notif.read
                            ? "bg-indigo-50/40 hover:bg-indigo-50/60"
                            : "hover:bg-slate-50"
                        }`}
                        onClick={() => handleNotifClick(notif)}
                      >
                        <div
                          className={`mt-0.5 shrink-0 h-9 w-9 rounded-lg ${iconConfig.bg} flex items-center justify-center`}
                        >
                          <Icon className={`h-4 w-4 ${iconConfig.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p
                              className={`text-sm ${!notif.read ? "font-semibold" : "font-medium"} text-slate-900`}
                            >
                              {notif.title}
                            </p>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${PRIORITY_BADGES[notif.priority]}`}
                            >
                              {notif.priority}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                            {notif.message}
                          </p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <p className="text-[11px] text-slate-400">
                              {formatNotifTime(notif.createdAt)}
                            </p>
                            {notif.channel?.email && notif.emailSent && (
                              <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                                <Mail className="h-3 w-3" /> Email sent
                              </span>
                            )}
                          </div>
                        </div>
                        {!notif.read && (
                          <div className="h-2 w-2 rounded-full bg-indigo-500 mt-2 shrink-0" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissMutation.mutate(notif._id);
                          }}
                          className="absolute right-2 top-2 p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-slate-200 text-slate-400 transition-all"
                          title="Dismiss"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
              <div className="border-t border-slate-100 px-4 py-2">
                <Link
                  to="/notifications"
                  onClick={() => setShowNotifications(false)}
                  className="block text-center text-xs font-medium text-slate-500 hover:text-slate-700 py-1"
                >
                  View all notifications
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="h-6 w-px bg-slate-200 mx-1" />

        {/* Org Switcher */}
        <div className="relative" ref={orgRef}>
          <button
            onClick={() => setShowOrgMenu(!showOrgMenu)}
            className="flex items-center gap-2.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-all"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-indigo-50">
              <Building2 className="h-3.5 w-3.5 text-indigo-600" />
            </div>
            <span className="max-w-[120px] truncate hidden sm:block">
              {activeOrganization?.name}
            </span>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          </button>

          {showOrgMenu && (
            <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 w-auto sm:w-64 rounded-2xl border border-slate-200 bg-white py-2 shadow-xl shadow-slate-200/50 z-50">
              <div className="px-3 py-2 border-b border-slate-100">
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                  Your Organizations
                </p>
              </div>
              {organizations?.length > 0 ? (
                organizations.map((org) => {
                  const orgId = org.id || org._id;
                  const activeId =
                    activeOrganization?.id || activeOrganization?._id;
                  const isActive = String(orgId) === String(activeId);

                  return (
                    <button
                      key={orgId}
                      onClick={() => !isActive && handleSwitch(orgId)}
                      className={`flex w-full items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                        isActive
                          ? "bg-indigo-50 text-indigo-700 cursor-default"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-xs font-bold text-indigo-700 shrink-0">
                        {getInitials(org.name)}
                      </div>
                      <div className="flex-1 text-left min-w-0">
                        <p className="font-medium truncate">{org.name}</p>
                        <p className="text-[11px] text-slate-500 capitalize">
                          {org.role || "member"}
                        </p>
                      </div>
                      {isActive && (
                        <CheckCircle2 className="h-4 w-4 text-indigo-600 shrink-0" />
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-4 text-center">
                  <p className="text-xs text-slate-400">
                    No organizations found
                  </p>
                </div>
              )}
              <div className="border-t border-slate-100 mt-1 pt-1 px-3">
                <Link
                  to="/organizations"
                  onClick={() => setShowOrgMenu(false)}
                  className="flex items-center gap-2 py-2 text-sm text-slate-600 hover:text-slate-900"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Manage organizations
                </Link>
              </div>
            </div>
          )}
        </div>

        {/* User Menu */}
        <div className="relative" ref={userRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2.5 rounded-xl p-1.5 hover:bg-slate-50 transition-colors"
          >
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-sm font-medium text-white">
              {getInitials(user?.contactName || user?.email)}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-slate-900 leading-tight">
                {user?.contactName || "User"}
              </p>
              <p className="text-[11px] text-slate-500 leading-tight truncate max-w-[120px]">
                {user?.email}
              </p>
            </div>
            <ChevronDown className="h-3.5 w-3.5 text-slate-400 hidden md:block" />
          </button>

          {showUserMenu && (
            <div className="fixed inset-x-3 top-16 sm:absolute sm:inset-auto sm:right-0 sm:top-full mt-2 w-auto sm:w-56 rounded-2xl border border-slate-200 bg-white py-2 shadow-xl shadow-slate-200/50 z-50">
              <div className="px-4 py-3 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900">
                  {user?.contactName || "User"}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">{user?.email}</p>
              </div>
              <div className="py-1">
                <Link
                  to="/settings"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Settings className="h-4 w-4 text-slate-400" />
                  Account Settings
                </Link>
                <Link
                  to="/settings?tab=gateways"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <CreditCard className="h-4 w-4 text-slate-400" />
                  Payment Methods & Gateways
                </Link>
                <Link
                  to="/settings/notifications"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Bell className="h-4 w-4 text-slate-400" />
                  Notification Preferences
                </Link>
                <Link
                  to="/billing"
                  onClick={() => setShowUserMenu(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <CreditCard className="h-4 w-4 text-slate-400" />
                  Billing
                </Link>
              </div>
              <div className="border-t border-slate-100 mt-1 pt-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    logout();
                  }}
                  className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
